/**
 * MODEL PREFERENCJI DOSTĘPNOŚCI — jedno źródło prawdy dla trzech konsumentów.
 *
 * Czytają go: warstwa stosująca (`a11y-preferences.applier.ts`), magazyn
 * (`a11y-preferences.store.ts`) i widget. Czwarty konsument NIE MOŻE go zaimportować,
 * bo biegnie przed bootstrapem Angulara: skrypt inline w `src/index.html` niesie RĘCZNĄ
 * KOPIĘ tych samych tablic. Dwie ręczne kopie tego samego kontraktu rozjeżdżają się —
 * dlatego `npm run verify:s22-p5` (krok 2) porównuje je DWUKIERUNKOWO i, dodatkowo,
 * URUCHAMIA skrypt inline dla każdej pary (klucz, wartość) i porównuje wywołane mutacje
 * dokumentu z tabelą efektów wyliczoną z tego pliku. Różnica w którąkolwiek stronę = czerwień.
 *
 * DLACZEGO WARTOŚCI LICZBOWE WCHODZĄ WYŁĄCZNIE PRZEZ `setProperty` NA KORZENIU.
 * `scripts/verify-s19-p1.mjs:96-100` szereguje deklaracje tokenów tylko w `html`/`:root`;
 * token zadeklarowany w klasie motywu albo w `@media` jest dla tamtej bramki NIESPRAWDZONY,
 * czyli czerwony. Nadpisanie inline na `document.documentElement` nie trafia do
 * skompilowanego CSS w ogóle, więc żadna bramka czytająca arkusze nie ma się o co potknąć —
 * a użytkownik i tak dostaje wartość wygrywającą kaskadę (styl inline bije każdy selektor).
 * Przełączniki idą odwrotną drogą: jako KLASY na `<html>` ustawiające zwykłe właściwości
 * w `src/styles/_a11y-preferences.scss`. Klasa bez deklaracji `--*` jest dla bramek niewidoczna.
 *
 * ŻADEN NOWY TOKEN. Wszystkie nazwy poniżej są własnością fazy 1 (`src/styles/_tokens.scss`,
 * `src/styles/_brand.scss`) i motywu M3 — ten plik je NADPISUJE w runtime, nie deklaruje.
 */

/** Konwencja nazewnicza z `src/app/features/course-signup/signup-draft.ts` (`empiria.signup.draft`). */
export const A11Y_PREFERENCES_KEY = 'empiria.a11y.preferences';

/**
 * SŁOWNIK KONTRAKTU: klucz → dopuszczalne wartości. Pierwsza wartość każdej listy jest
 * wartością domyślną (patrz `A11Y_PREFERENCE_DEFAULTS`) i oznacza „ta preferencja nic nie robi".
 *
 * Wszystko jest tu tekstem, także przełączniki dwustanowe (`off`/`on`) — bo drugą stroną
 * kontraktu jest skrypt ES5 bez typów, a zbiór łańcuchów porównuje się bez konwersji.
 */
export const A11Y_PREFERENCE_VALUES = {
  textSize: ['normal', 'smaller', 'large', 'larger', 'largest'],
  contrast: ['normal', 'high', 'maximum'],
  grayscale: ['off', 'on'],
  dyslexia: ['off', 'on'],
  underlineLinks: ['off', 'on'],
  stopAnimations: ['off', 'on'],
  cursorSize: ['normal', 'large', 'larger', 'largest'],
} as const;

export type A11yPreferenceKey = keyof typeof A11Y_PREFERENCE_VALUES;

export type A11yPreferenceValue<K extends A11yPreferenceKey> =
  (typeof A11Y_PREFERENCE_VALUES)[K][number];

export type A11yPreferences = { [K in A11yPreferenceKey]: A11yPreferenceValue<K> };

/** Pierwsza wartość listy — ta, która znaczy „ta preferencja nic nie robi". */
export type A11yPreferenceDefault<K extends A11yPreferenceKey> =
  (typeof A11Y_PREFERENCE_VALUES)[K][0];

/**
 * Wartości, które COŚ robią. To po tej unii — a nie po gołym `string` — szeregowane są
 * wszystkie tabele efektów niżej: wpis dla wartości domyślnej byłby martwym kodem
 * (`a11yEffectFor` zwraca dla niej `NO_EFFECT`, zanim spojrzy w którąkolwiek tabelę),
 * a brak wpisu dla wartości niedomyślnej to dokładnie ta cisza, którą ten plik ma
 * wykluczyć.
 */
export type A11yNonDefaultValue<K extends A11yPreferenceKey> = Exclude<
  A11yPreferenceValue<K>,
  A11yPreferenceDefault<K>
>;

/**
 * CZYM preferencja jest realizowana. Cztery mechanizmy, bo tyle ich jest — i to ta
 * tabela, a nie łańcuch `if`ów, jest miejscem, w którym ósma preferencja MUSI podjąć
 * decyzję: `Record` po unii kluczy nie skompiluje się bez wpisu, a `switch` niżej nie
 * skompiluje się bez gałęzi dla nowego mechanizmu (`never` w `default`).
 *
 * PRZED TĄ TABELĄ (impl-review S-22 F14) słowniki miały klucz `string`, więc ósma
 * preferencja przechodziła kompilację, lint i spece — i nie robiła nic. Spece tego nie
 * łapały z założenia: obie strony asercji wyprowadzały się z tej samej tabeli, więc brak
 * wpisu znaczył „pusto po obu stronach", czyli zgodę.
 */
export const A11Y_PREFERENCE_MECHANISM = {
  textSize: 'scale',
  contrast: 'contrast',
  grayscale: 'class',
  dyslexia: 'class',
  underlineLinks: 'class',
  stopAnimations: 'motion',
  cursorSize: 'class',
} as const satisfies Readonly<Record<A11yPreferenceKey, 'scale' | 'contrast' | 'motion' | 'class'>>;

type A11yMechanism = (typeof A11Y_PREFERENCE_MECHANISM)[A11yPreferenceKey];

export const A11Y_PREFERENCE_KEYS = Object.keys(A11Y_PREFERENCE_VALUES) as readonly A11yPreferenceKey[];

/**
 * Domyślne = pierwsza wartość z listy. Wyliczane, nie przepisywane: druga ręczna kopia
 * tej samej listy jest dokładnie tym błędem, przed którym ostrzega nagłówek tego pliku.
 */
export const A11Y_PREFERENCE_DEFAULTS: A11yPreferences = Object.fromEntries(
  A11Y_PREFERENCE_KEYS.map((key) => [key, A11Y_PREFERENCE_VALUES[key][0]]),
) as A11yPreferences;

/**
 * SKALA TEKSTU — mnożniki pięciu stopni.
 *
 * `normal` = 1 i nie generuje ani jednego nadpisania, więc stan domyślny aplikacji jest
 * dosłownie stanem bez ingerencji. Zakres 0.9-1.5 pochodzi z §5.1 kontraktu fazy.
 */
export const A11Y_TEXT_SCALES: Readonly<Record<A11yPreferenceValue<'textSize'>, number>> = {
  normal: 1,
  smaller: 0.9,
  large: 1.15,
  larger: 1.3,
  largest: 1.5,
};

/**
 * DRABINA SKALOWANYCH TOKENÓW: nazwa tokenu → jego wartość bazowa w `rem`.
 *
 * To jest KOPIA literałów z `src/styles/_tokens.scss:95-114` — i jedyna kopia w tym slice,
 * która NIE jest sprawdzana ręcznie: `verify:s22-p5` kompiluje `src/styles.scss` sassem
 * i porównuje każdą pozycję poniżej z wartością, która faktycznie trafiła do CSS. Rozjazd
 * po którejkolwiek stronie zapala bramkę, zamiast po cichu skalować nie tę drabinę.
 *
 * Interlinie skalują się RAZEM z rozmiarami. Bez tego tekst przy mnożniku 1.5 wchodzi
 * sam na siebie — a to jest regres dostępności popełniony przez funkcję dostępności.
 *
 * KLUCZ NIE JEST GOŁYM `string`, ale też nie jest unią: nazwy tokenów należą do warstwy
 * miar (`src/styles/_tokens.scss`, własność fazy 1), a ten plik ich nie posiada. Typ
 * szablonowy odrzuca więc to, co da się odrzucić statycznie — token spoza dwóch drabin,
 * które ta preferencja skaluje — a KOMPLETNOŚĆ drabiny sprawdza bramka po wartościach
 * ze SKOMPILOWANEGO CSS (`verify:s22-p5`, krok 2), czyli mocniej, niż zrobiłby to typ.
 */
type A11yScaledToken = `--empiria-font-size-${string}` | `--empiria-line-height-${string}`;

export const A11Y_SCALED_TOKENS: Readonly<Record<A11yScaledToken, number>> = {
  '--empiria-font-size-2xs': 0.688,
  '--empiria-font-size-xs': 0.75,
  '--empiria-font-size-sm': 0.875,
  '--empiria-font-size-md': 1,
  '--empiria-font-size-lead': 1.125,
  '--empiria-font-size-lg': 1.375,
  '--empiria-font-size-xl': 1.5,
  '--empiria-font-size-2xl': 2,
  '--empiria-font-size-3xl': 3,
  '--empiria-line-height-compact': 1,
  '--empiria-line-height-snug': 1.25,
  '--empiria-line-height-normal': 1.5,
  '--empiria-line-height-relaxed': 1.75,
  '--empiria-line-height-xl': 2,
  '--empiria-line-height-2xl': 2.5,
  '--empiria-line-height-3xl': 3.5,
};

/**
 * POZIOMY KONTRASTU — nadpisania ról koloru, mierzone, nie wyczute.
 *
 * ⚠️ OBEJMUJĄ TAKŻE TOKENY MARKI, nie tylko role `--mat-sys-*`. `--empiria-brand-accent`
 * jest obwódką fokusu (`:focus-visible`, faza 2), a jego zmierzone 3.85:1 na bieli
 * (`src/styles/_brand.scss:9-17`) przestaje cokolwiek gwarantować, gdy tło pod nim się
 * zmienia. Żaden automat tego nie złapie: `verify:s19-p5` czyta SKOMPILOWANY CSS, więc
 * nadpisań runtime nie widzi z definicji, a axe-core nie ma reguły na kontrast wskaźnika
 * fokusu w tagach `wcag2a`/`wcag2aa`/`wcag21a`/`wcag21aa`. Dowodem jest kryterium manualne 5.13
 * oraz liczby wypisane niżej (wzór WCAG 2.x, tło = powierzchnia danego poziomu):
 *
 *   poziom `high`     — on-surface 21.00:1 · on-surface-variant 17.04:1 · outline 14.16:1
 *                       primary/on-primary 10.31:1 · error/on-error 8.21:1
 *                       tertiary/on-tertiary 9.67:1 · brand-accent 6.46:1 (na bieli)
 *                       brand-accent 5.57:1 (na kontenerze #eeeeee) · brand-text 9.19:1
 *                       status-info 10.87:1 · status-warning 11.32:1 (tekst na swoim podkładzie)
 *   poziom `maximum`  — on-surface 21.00:1 · primary 14.38:1 · error 12.92:1
 *                       tertiary 14.05:1 · brand-accent 14.38:1
 *                       status-info 14.05:1 · status-warning 15.06:1
 *
 * ⚠️ OBEJMUJĄ TAKŻE TONY PIGUŁKI STATUSU (`--empiria-status-*`, S-22 faza 1). Na poziomie
 * `maximum` podkład pigułki staje się BIAŁY, czyli ton przestaje być widoczny jako
 * powierzchnia — i to jest poprawne, a nie ubytek: znaczenie statusu niosą tam nadal glif
 * i słowo, dokładnie tak jak w trybie skali szarości i na wydruku czarno-białym. Gdyby ton
 * był jedynym nośnikiem, ten poziom kontrastu kasowałby informację.
 *
 * Poziom `maximum` zachowuje ODCIEŃ ról znaczeniowych (zieleń, czerwień, błękit) zamiast
 * spłaszczać wszystko do czerni: barwa nie jest tu jedynym nośnikiem znaczenia (WCAG 1.4.1
 * pilnuje `verify:s19-p4`), ale jej utrata bez potrzeby pogarsza czytelność statusów.
 */
export const A11Y_CONTRAST_OVERRIDES: Readonly<
  Record<A11yNonDefaultValue<'contrast'>, Readonly<Record<string, string>>>
> = {
  high: {
    '--mat-sys-background': '#ffffff',
    '--mat-sys-on-background': '#000000',
    '--mat-sys-surface': '#ffffff',
    '--mat-sys-surface-bright': '#ffffff',
    '--mat-sys-surface-dim': '#e6e6e6',
    '--mat-sys-surface-variant': '#e6e6e6',
    '--mat-sys-surface-container-lowest': '#ffffff',
    '--mat-sys-surface-container-low': '#f6f6f6',
    '--mat-sys-surface-container': '#eeeeee',
    '--mat-sys-surface-container-high': '#e6e6e6',
    '--mat-sys-surface-container-highest': '#dcdcdc',
    '--mat-sys-on-surface': '#000000',
    '--mat-sys-on-surface-variant': '#1c1c1c',
    '--mat-sys-outline': '#2b2b2b',
    '--mat-sys-outline-variant': '#5c5c5c',
    '--mat-sys-primary': '#0f4b14',
    '--mat-sys-on-primary': '#ffffff',
    '--mat-sys-primary-container': '#d6f5d1',
    '--mat-sys-on-primary-container': '#00300a',
    '--mat-sys-secondary': '#24421f',
    '--mat-sys-on-secondary': '#ffffff',
    '--mat-sys-secondary-container': '#d3ecce',
    '--mat-sys-on-secondary-container': '#12300f',
    '--mat-sys-tertiary': '#004a66',
    '--mat-sys-on-tertiary': '#ffffff',
    '--mat-sys-tertiary-container': '#d4eeff',
    '--mat-sys-on-tertiary-container': '#00354a',
    '--mat-sys-error': '#a30000',
    '--mat-sys-on-error': '#ffffff',
    '--mat-sys-error-container': '#ffe3df',
    '--mat-sys-on-error-container': '#6b0000',
    '--empiria-brand-accent': '#2d6b2c',
    '--empiria-brand-text': '#1f5220',
    '--empiria-status-info-surface': '#d4eeff',
    '--empiria-status-info-text': '#00354a',
    '--empiria-status-warning-surface': '#ffe9c9',
    '--empiria-status-warning-text': '#4a2600',
  },
  maximum: {
    '--mat-sys-background': '#ffffff',
    '--mat-sys-on-background': '#000000',
    '--mat-sys-surface': '#ffffff',
    '--mat-sys-surface-bright': '#ffffff',
    '--mat-sys-surface-dim': '#ffffff',
    '--mat-sys-surface-variant': '#ffffff',
    '--mat-sys-surface-container-lowest': '#ffffff',
    '--mat-sys-surface-container-low': '#ffffff',
    '--mat-sys-surface-container': '#ffffff',
    '--mat-sys-surface-container-high': '#ffffff',
    '--mat-sys-surface-container-highest': '#ffffff',
    '--mat-sys-on-surface': '#000000',
    '--mat-sys-on-surface-variant': '#000000',
    '--mat-sys-outline': '#000000',
    '--mat-sys-outline-variant': '#000000',
    '--mat-sys-primary': '#00320a',
    '--mat-sys-on-primary': '#ffffff',
    '--mat-sys-primary-container': '#ffffff',
    '--mat-sys-on-primary-container': '#00320a',
    '--mat-sys-secondary': '#00320a',
    '--mat-sys-on-secondary': '#ffffff',
    '--mat-sys-secondary-container': '#ffffff',
    '--mat-sys-on-secondary-container': '#00320a',
    '--mat-sys-tertiary': '#00303f',
    '--mat-sys-on-tertiary': '#ffffff',
    '--mat-sys-tertiary-container': '#ffffff',
    '--mat-sys-on-tertiary-container': '#00303f',
    '--mat-sys-error': '#6b0000',
    '--mat-sys-on-error': '#ffffff',
    '--mat-sys-error-container': '#ffffff',
    '--mat-sys-on-error-container': '#6b0000',
    '--empiria-brand-accent': '#00320a',
    '--empiria-brand-text': '#00320a',
    '--empiria-status-info-surface': '#ffffff',
    '--empiria-status-info-text': '#00303f',
    '--empiria-status-warning-surface': '#ffffff',
    '--empiria-status-warning-text': '#3d1f00',
  },
};

/**
 * ZATRZYMANIE ANIMACJI — oba tokeny czasu, nie jeden.
 *
 * §5.1 kontraktu wymienia tylko `--empiria-motion-duration`, ale `-slow` jest osobnym
 * tokenem z własnym konsumentem (`.mat-mdc-card` w `src/styles.scss`). Zerowanie jednego
 * z dwóch hamulców zostawiłoby ruch tam, gdzie jest największy.
 */
export const A11Y_MOTION_OVERRIDES: Readonly<Record<string, string>> = {
  '--empiria-motion-duration': '0s',
  '--empiria-motion-duration-slow': '0s',
};

/**
 * PRZEŁĄCZNIKI I STOPNIE REALIZOWANE KLASĄ NA `<html>`.
 * Reguły stoją w `src/styles/_a11y-preferences.scss` (partial wpięty przez fazę 1 na końcu
 * kaskady `src/styles.scss`) i deklarują wyłącznie zwykłe właściwości — ani jednego `--*`.
 *
 * TABELA JEST KOMPLETNA PO KLUCZACH, A DLA KLUCZY KLASOWYCH — TAKŻE PO WARTOŚCIACH.
 * Typ mapowany rozgałęzia się po `A11Y_PREFERENCE_MECHANISM`: preferencja oznaczona jako
 * `'class'` musi wymienić KAŻDĄ swoją wartość niedomyślną (dziesiąty stopień kursora bez
 * klasy = błąd kompilacji), a preferencja realizowana skalą, kontrastem albo ruchem musi
 * stać tu z pustym obiektem — bo `Record<never, string>` nie przyjmuje żadnego klucza.
 * Puste `{}` przy trzech kluczach jest więc ASERCJĄ („tę realizuje inny mechanizm"),
 * a nie przeoczeniem.
 *
 * WARTOŚCIĄ JEST WYŁĄCZNIE `string`, nigdy `null`: `verify:s22-p5` (krok 2) ewaluuje ten
 * literał w piaskownicy i czyta brak wpisu jako `undefined`. `null` przeszedłby przez
 * `?.` i wjechał na listę klas jako pusty żeton.
 */
export const A11Y_STATE_CLASSES: {
  readonly [K in A11yPreferenceKey]: (typeof A11Y_PREFERENCE_MECHANISM)[K] extends 'class'
    ? Readonly<Record<A11yNonDefaultValue<K>, string>>
    : Readonly<Record<never, string>>;
} = {
  textSize: {},
  contrast: {},
  stopAnimations: {},
  grayscale: { on: 'empiria-a11y-grayscale' },
  dyslexia: { on: 'empiria-a11y-dyslexia' },
  underlineLinks: { on: 'empiria-a11y-underline-links' },
  cursorSize: {
    large: 'empiria-a11y-cursor-large',
    larger: 'empiria-a11y-cursor-larger',
    largest: 'empiria-a11y-cursor-largest',
  },
};

/** Wszystkie klasy stanów — do zdjęcia z `<html>` przed każdym ponownym zastosowaniem. */
export const A11Y_ALL_STATE_CLASSES: readonly string[] = Object.values(A11Y_STATE_CLASSES).flatMap(
  (byValue) => Object.values(byValue),
);

/** Wszystkie nadpisywane custom properties — do zdjęcia z korzenia przed ponownym zastosowaniem. */
export const A11Y_ALL_OVERRIDDEN_PROPERTIES: readonly string[] = [
  ...Object.keys(A11Y_SCALED_TOKENS),
  ...Object.keys(A11Y_MOTION_OVERRIDES),
  ...new Set(Object.values(A11Y_CONTRAST_OVERRIDES).flatMap((level) => Object.keys(level))),
];

/**
 * Mutacje dokumentu, jakie wywołuje POJEDYNCZA para (klucz, wartość).
 *
 * Wydzielone z warstwy stosującej, bo to jest właśnie TA połowa kontraktu, którą powtarza
 * skrypt inline — i którą bramka porównuje, uruchamiając obie strony zamiast czytać je regexem.
 */
export interface A11yEffect {
  readonly properties: Readonly<Record<string, string>>;
  readonly classes: readonly string[];
}

const NO_EFFECT: A11yEffect = { properties: {}, classes: [] };

/** `0.688 × 1.15 = 0.7912` → `'0.791rem'`. Ta sama funkcja po obu stronach kontraktu. */
function scaledRem(base: number, scale: number): string {
  return `${Math.round(base * scale * 1000) / 1000}rem`;
}

/** Cała drabina przeskalowana jednym mnożnikiem — jedyny efekt stopnia rozmiaru tekstu. */
function scaleEffect(value: A11yNonDefaultValue<'textSize'>): A11yEffect {
  const scale = A11Y_TEXT_SCALES[value];
  const properties: Record<string, string> = {};

  for (const [token, base] of Object.entries(A11Y_SCALED_TOKENS)) {
    properties[token] = scaledRem(base, scale);
  }

  return { properties, classes: [] };
}

/**
 * ROZDZIELNIK MECHANIZMÓW, wyczerpujący z definicji: `default` przyjmuje `never`, więc
 * piąty mechanizm dopisany do `A11Y_PREFERENCE_MECHANISM` przestaje się kompilować,
 * dopóki nie dostanie własnej gałęzi. Wcześniej ten sam rozdział był łańcuchem `if`ów po
 * nazwach kluczy, a ostatni `if` był domyślną furtką „to pewnie klasa" — czyli miejscem,
 * w którym ósma preferencja wpadała w ciszę zamiast w błąd (impl-review S-22 F14).
 *
 * RZUTOWANIA WARTOŚCI SĄ ŚWIADOME I ZAMKNIĘTE W TEJ JEDNEJ FUNKCJI. TypeScript nie zawęża
 * `value` przy zawężaniu mechanizmu, bo `K` pozostaje generyczne; zawężenie robi tabela,
 * która przypisała kluczowi ten mechanizm. Wejście do funkcji jest w pełni typowane, a
 * `value` jest tu już po odrzuceniu wartości domyślnej.
 */
export function a11yEffectFor<K extends A11yPreferenceKey>(
  key: K,
  value: A11yPreferenceValue<K>,
): A11yEffect {
  if (value === A11Y_PREFERENCE_DEFAULTS[key]) {
    return NO_EFFECT;
  }

  const mechanism: A11yMechanism = A11Y_PREFERENCE_MECHANISM[key];

  switch (mechanism) {
    case 'scale':
      return scaleEffect(value as A11yNonDefaultValue<'textSize'>);

    case 'contrast':
      return {
        properties: A11Y_CONTRAST_OVERRIDES[value as A11yNonDefaultValue<'contrast'>],
        classes: [],
      };

    case 'motion':
      return { properties: A11Y_MOTION_OVERRIDES, classes: [] };

    case 'class': {
      const byValue: Readonly<Record<string, string | undefined>> = A11Y_STATE_CLASSES[key];
      const stateClass = byValue[value];

      return stateClass === undefined ? NO_EFFECT : { properties: {}, classes: [stateClass] };
    }

    default:
      return unreachableMechanism(mechanism);
  }
}

/** Piąty mechanizm bez gałęzi w `switch` nie skompiluje się tutaj. */
function unreachableMechanism(mechanism: never): never {
  throw new Error(`Preferencja dostępności o nieobsłużonym mechanizmie: ${String(mechanism)}`);
}

/** Czy `value` należy do słownika `key`. Wartość spoza słownika traktujemy jak BRAK, nie jak błąd. */
export function isA11yPreferenceValue<K extends A11yPreferenceKey>(
  key: K,
  value: unknown,
): value is A11yPreferenceValue<K> {
  return typeof value === 'string' && (A11Y_PREFERENCE_VALUES[key] as readonly string[]).includes(value);
}

export function isA11yPreferenceKey(key: unknown): key is A11yPreferenceKey {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(A11Y_PREFERENCE_VALUES, key);
}

export function createDefaultA11yPreferences(): A11yPreferences {
  return { ...A11Y_PREFERENCE_DEFAULTS };
}

/** Czy cokolwiek odbiega od domyślnych — steruje widocznością „przywróć wszystko". */
export function hasNonDefaultA11yPreferences(preferences: A11yPreferences): boolean {
  return A11Y_PREFERENCE_KEYS.some((key) => preferences[key] !== A11Y_PREFERENCE_DEFAULTS[key]);
}
